<script setup lang="ts">
import type { ChartOptions } from 'chart.js'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  LineElement,
  PointElement,
} from 'chart.js'
import IconBenchmarkLogoWatermark from '@/assets/icons/benchmark-logo-watermark.svg'
import HorizontalBarChart from '@/components/charts/HorizontalBarChart.vue'
import VerticalBarChart from '@/components/charts/VerticalBarChart.vue'
import PieChart from '@/components/charts/PieChart.vue'
import LineChart from '@/components/charts/LineChart.vue'
import { formatText, setAxisTitles } from '@/utils/chart'
import { logError } from '@/utils/log-error'
import type { ChartData as ChartDataInterface } from '~/models/statistics'

import IconChartDecoration from '@/assets/icons/chart-decoration.svg'

const { $theme } = useNuxtApp()

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  LineElement,
  PointElement,
)

const props = withDefaults(
  defineProps<{
    data: ChartDataInterface
    containerClasses?: string
    chartClasses?: string
    legendClasses?: string
    hideYAxis?: boolean
    axisFontSize?: number
  }>(),
  {
    containerClasses: '',
    chartClasses: '',
    legendClasses: '',
    hideYAxis: false,
    axisFontSize: 12,
  },
)

const emit = defineEmits(['chartRef'])

const chartOptions = ref(props.data['chart-options'])
const ChartData = ref(props.data.chart_data)

function linkSecondYAxis(options, data) {
  if (options.manual === '') return

  let manualConfig

  try {
    manualConfig = JSON.parse(options.manual)
  } catch (e) {
    return logError(e)
  }

  if (!manualConfig.secondYAxis) return

  const dataset = data.datasets.find(
    (dataset) => dataset.label === manualConfig.secondYAxis,
  )

  if (!dataset) return
  dataset.yAxisID = 'secondYAxis'

  return {
    type: 'linear',
    display: true,
    position: 'right',
    grid: {
      drawOnChartArea: false,
    },
  }
}

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
const secondYAxis: void | {
  type: string
  display: boolean
  position: string
  grid: { drawOnChartArea: boolean }
} = linkSecondYAxis(chartOptions.value, ChartData.value)

const isStacked =
  chartOptions.value.xAxes?.stacked_bool === 'true' ||
  chartOptions.value.yAxes?.stacked_bool === 'true'

const charts = {
  bar: HorizontalBarChart,
  column: VerticalBarChart,
  pie: PieChart,
  line: LineChart,
}

const font = {
  family: 'Montserrat',
  size: props.axisFontSize,
}
const yAxisFont = {
  family: 'Montserrat',
  size: 0,
}

const options = {
  responsive: true,
  maintainAspectRatio: false,

  elements: {
    point: {
      radius: 0,
    },
  },

  scales: {
    y: {
      stacked: isStacked,
      ticks: {
        color: $theme.colors.s['900'],
        font: props.hideYAxis ? yAxisFont : font,
        callback(value) {
          return formatText(value)
        },
      },
      beginAtZero: true,
    },

    ...(secondYAxis && { secondYAxis }),

    x: {
      stacked: isStacked,
      afterFit: function (scale) {
        scale.width = 220
      },
      grid: {
        display: false,
      },
      ticks: {
        padding: 0,
        color: $theme.colors.s['900'],
        font,
        callback: function (_val, index) {
          return formatText(props.data.chart_data.labels[index])
        },
      },
    },
  },

  plugins: {
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        title([context]) {
          return formatText(context.label)
        },
        label(context: Record<string, object>) {
          const label = formatText(context.raw)
          return ` ${context.dataset.label}: ${label}`
        },
      },
    },

    legend: {
      display: false,
    },
  },
} as ChartOptions

if (props.data.type !== 'pie')
  setAxisTitles(chartOptions.value, options, $theme.colors.s['900'], {
    ...font,
    weight: '600',
  })

const title = chartOptions.value.title.text
const description = chartOptions.value.description
const source = chartOptions.value.creator

onWindowResize(() => updated.value++)
const updated = ref(0)
</script>

<template>
  <figure class="bg-white">
    <div class="flex">
      <div>
        <IconChartDecoration
          class="mr-2 h-10 w-auto md:mr-4 md:h-20 md:w-auto"
          data-testid="chart-decoration"
        />
      </div>

      <div class="mt-0 md:mt-8" data-testid="chart-title-container">
        <p
          v-if="title"
          class="m-0 text-xl font-semibold text-s-700 md:text-2xl"
          :title="title"
          data-testid="chart-title"
        >
          {{ title }}
        </p>

        <figcaption v-if="description" class="m-0 text-sm text-s-700">
          <span
            class="line-clamp-4 block text-left text-xs md:line-clamp-2"
            data-testid="chart-description"
          >
            {{ description }}
          </span>
        </figcaption>
      </div>
    </div>

    <component
      :is="charts[data.type]"
      v-if="data"
      :key="updated"
      class="mt-7"
      :class="containerClasses"
      :chart-classes="chartClasses"
      :legend-classes="legendClasses"
      :data="data['chart_data']"
      :default-options="options"
      :visualizer-options="data['chart-options']"
      @chart-ref="(chartRef) => emit('chartRef', chartRef)"
    />

    <section data-testid="chart-watermark" class="flex items-end gap-x-4 p-2">
      <p v-if="source" class="my-0 grow text-xxs uppercase">
        Source: {{ source }}
      </p>

      <IconBenchmarkLogoWatermark class="mb-1 shrink-0" />
    </section>
  </figure>
</template>
